import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { CONSTANTS } from '@livable/saas-react-components';
import moment from 'moment';

/** How bills reach Livable, as the PRD names each source (FR-6). */
export const BILL_SOURCE_LABELS = {
  Email: 'Email',
  UtilityPortal: 'Utility portal',
  Upload: 'Upload to Livable Pro',
  NotSent: 'No bills sent to Livable'
};
const FREQUENCY_LABELS = {
  Monthly: 'Monthly',
  BiMonthly: 'Every 2 months',
  Trimonthly: 'Quarterly',
  Annually: 'Annually'
};

/** What each row means, as the PRD mockup says it (FR-11). */
export const SETUP_TIPS = {
  setup: 'How the company handles this kind of utility. Every utility linked to it follows it. This utility keeps its own ' + 'provider, account number, meters, portal account, deductions, vacant units, bill ready day, GL accounts and ' + 'charge rules.',
  source: 'Your plan decides which ways are open. The others show the plan they need.',
  billToResidents: "Livable allocates each bill to residents with this setup's allocation method, and the charges go on their " + 'statements.',
  frequency: "Used to know when to expect each bill, fetch it, and send late alerts. It doesn't split a bill into monthly charges.",
  apPush: "Livable sends the bill to AppFolio as a payable once it's approved. One invoice goes as one payable, so utilities " + 'on one invoice share this setting. AP Push is a metered add-on for Plus and Autopilot, counted per bill pushed.'
};
export const sourceLabel = billSource => {
  var _BILL_SOURCE_LABELS$b;
  return (_BILL_SOURCE_LABELS$b = BILL_SOURCE_LABELS[billSource]) !== null && _BILL_SOURCE_LABELS$b !== void 0 ? _BILL_SOURCE_LABELS$b : 'Not chosen yet';
};
export const frequencyLabel = frequency => {
  var _ref, _FREQUENCY_LABELS$fre;
  return (_ref = (_FREQUENCY_LABELS$fre = FREQUENCY_LABELS[frequency]) !== null && _FREQUENCY_LABELS$fre !== void 0 ? _FREQUENCY_LABELS$fre : frequency) !== null && _ref !== void 0 ? _ref : '';
};
export const methodLabel = method => {
  var _ref2, _CONSTANTS$ALLOCATION, _CONSTANTS$ALLOCATION2;
  return (_ref2 = (_CONSTANTS$ALLOCATION = (_CONSTANTS$ALLOCATION2 = CONSTANTS.ALLOCATION_METHODS) === null || _CONSTANTS$ALLOCATION2 === void 0 ? void 0 : _CONSTANTS$ALLOCATION2[method]) !== null && _CONSTANTS$ALLOCATION !== void 0 ? _CONSTANTS$ALLOCATION : method) !== null && _ref2 !== void 0 ? _ref2 : '';
};

/** "On · Occupancy". Off isn't offered until Phase 2, but a setup could still hold it. */
export const billToResidentsLabel = _ref3 => {
  let billToResidents = _ref3.billToResidents,
    allocationMethod = _ref3.allocationMethod;
  return billToResidents ? "On \xB7 ".concat(methodLabel(allocationMethod)) : 'Off';
};
const count = (n, one, many) => "".concat(n, " ").concat(n === 1 ? one : many);

/** "14 utilities at 9 properties". */
export const usedByLabel = _ref4 => {
  let utilities = _ref4.utilities,
    properties = _ref4.properties;
  return "".concat(count(utilities, 'utility', 'utilities'), " at ").concat(count(properties, 'property', 'properties'));
};
export const dateLabel = value => moment(value).format('MMM D, YYYY');

/** A yyyy-MM-dd day, read as that calendar day wherever the browser is. */
export const dayLabel = value => moment(value, 'YYYY-MM-DD').format('MMM D, YYYY');

/** "Launch seed, Oct 24, 2026", or null when the setup holds no change time. */
export const lastChangeLabel = _ref5 => {
  let changedByName = _ref5.changedByName,
    changedOn = _ref5.changedOn;
  if (!changedOn) return null;
  return changedByName ? "".concat(changedByName, ", ").concat(dateLabel(changedOn)) : dateLabel(changedOn);
};

/** The rows a pending edit changes, in the summary's order. */
export const pendingEditChanges = setup => {
  const edit = setup === null || setup === void 0 ? void 0 : setup.pendingEdit;
  if (!edit) return [];
  return [['How bills reach Livable', edit.billSource !== setup.billSource], ['Bill to residents', edit.billToResidents !== setup.billToResidents], ['Allocation method', edit.allocationMethod !== setup.allocationMethod], ['Bill frequency', edit.billFrequencyType !== setup.billFrequencyType], ['AP Push', edit.isApPushEnabled !== setup.isApPushEnabled]].filter(_ref6 => {
    let _ref7 = _slicedToArray(_ref6, 2),
      changed = _ref7[1];
    return changed;
  }).map(_ref8 => {
    let _ref9 = _slicedToArray(_ref8, 1),
      label = _ref9[0];
    return label;
  });
};

/**
 * The plan a source needs when the company's plan doesn't include it: email needs Plus, the utility portal Autopilot.
 * Null when it's included, or while the plan isn't known.
 */
export const sourceNeeds = (billSource, allows) => {
  if (!allows) return null;
  if (billSource === 'Email' && !allows.emailIn) return 'Plus';
  if (billSource === 'UtilityPortal' && !allows.portal) return 'Autopilot';
  return null;
};

/** AP Push is an add-on for Plus and Autopilot only (FR-28). Null when it's included, or while the plan isn't known. */
export const apPushNeeds = allows => allows && !allows.apPush ? 'Plus' : null;